import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { apiErrorText, useImportRegistryDocumentMutation } from '@shared/api';
import { decodeStatement } from '@shared/lib/decodeStatement';
import { Button } from '@shared/ui/Button';
import { IconFolderOpen, IconLightbulb, IconWand, IconX } from '@shared/ui/icons';
import { describeImportFile, type ImportFileInfo } from './importFile';

interface Props {
  open: boolean;
  onClose: () => void;
  activeRegistryId?: string | null;
  onSuccess?: () => void;
}

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
  padding: 16px;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  width: 100%;
  max-width: 720px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  display: flex;
  flex-direction: column;
`;

const ModalHeader = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 17px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ModalBody = styled.div`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const FormatToggleRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const FormatTab = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid ${({ $active }) => ($active ? '#10b981' : '#334155')};
  background: ${({ $active }) => ($active ? '#10b98122' : 'transparent')};
  color: ${({ $active, theme }) => ($active ? '#10b981' : theme.colors.text.secondary)};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;

  &:hover {
    border-color: #10b981;
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  height: 220px;
  padding: 10px 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.background};
  color: ${({ theme }) => theme.colors.text.primary};
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
  font-size: 12px;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #10b981;
  }
`;

const Hint = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 6px;

  & > svg {
    flex-shrink: 0;
    margin-top: 1px;
  }
`;

const FileUploadRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ModalFooter = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`;

const ErrorBox = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #ef444422;
  border: 1px solid #ef4444;
  color: #ef4444;
  font-size: 12px;
  white-space: pre-line;
`;

const SuccessBox = styled.div`
  padding: 8px 12px;
  border-radius: 6px;
  background: #10b98122;
  border: 1px solid #10b981;
  color: #10b981;
  font-size: 12px;
  font-weight: 600;
  white-space: pre-line;
`;

const FileInfo = styled.div<{ $warn: boolean }>`
  font-size: 12px;
  color: ${({ $warn, theme }) => ($warn ? theme.colors.status.warning : theme.colors.text.secondary)};
`;

export function ImportRegistry1CModal({ open, onClose, activeRegistryId, onSuccess }: Props) {
  const [importDoc, { isLoading }] = useImportRegistryDocumentMutation();
  const [format, setFormat] = useState<'auto' | '1c_client_bank' | 'tsv' | 'json'>('auto');
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [resultMessage, setResultMessage] = useState<string | null>(null);
  // Что прочиталось из выбранного файла: имя и сколько в нём документов или строк.
  const [fileInfo, setFileInfo] = useState<ImportFileInfo | null>(null);

  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  // Файл 1С и выписки банков чаще в Windows-1251, реже в UTF-8: читаем байты и
  // определяем кодировку (`decodeStatement`), иначе кириллица файла теряется.
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const file = input.files?.[0];
    if (!file) return;
    setError(null);
    setResultMessage(null);
    try {
      const text = decodeStatement(await file.arrayBuffer());
      const info = describeImportFile(file.name, text);
      setContent(text);
      setFileInfo(info);
      if (info.format !== 'auto') setFormat(info.format);
    } catch {
      setFileInfo(null);
      setError(`Файл «${file.name}» не прочитан — проверьте его и выберите снова`);
    } finally {
      // Тот же файл можно выбрать ещё раз (после правки на диске)
      input.value = '';
    }
  };

  const handleImport = async () => {
    if (!content.trim()) {
      setError('Вставьте или загрузите содержимое файла для импорта');
      return;
    }

    try {
      setError(null);
      setResultMessage(null);
      const res = await importDoc({
        content: content.trim(),
        format,
        registry_id: activeRegistryId || undefined,
      }).unwrap();

      const found = fileInfo?.count;
      const created = res.created_requests_count;
      const skipped =
        found != null && found > created
          ? `\nНе взято в реестр: ${found - created} (без суммы или без получателя)`
          : '';
      setResultMessage(
        `Импортировано заявок: ${found != null ? `${created} из ${found} в файле` : created}, ` +
          `контрагентов: ${res.created_counterparties_count}${skipped}`
      );
      if (onSuccess) onSuccess();
    } catch (err: unknown) {
      setError(apiErrorText(err, 'Не удалось импортировать реестр'));
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <ModalHeader>
          <ModalTitle>Импорт Реестра платежей и Контрагентов</ModalTitle>
          <Button $variant="ghost" $size="sm" onClick={onClose} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </ModalHeader>

        <ModalBody>
          {error && <ErrorBox role="alert">{error}</ErrorBox>}

          {resultMessage && <SuccessBox role="status">{resultMessage}</SuccessBox>}

          <FormatToggleRow>
            <FormatTab $active={format === 'auto'} onClick={() => setFormat('auto')}>
              <IconWand size={13} />
              Автоопределение
            </FormatTab>
            <FormatTab $active={format === '1c_client_bank'} onClick={() => setFormat('1c_client_bank')}>
              1CClientBankExchange
            </FormatTab>
            <FormatTab $active={format === 'tsv'} onClick={() => setFormat('tsv')}>
              Excel (TSV / CSV скопированные ячейки)
            </FormatTab>
            <FormatTab $active={format === 'json'} onClick={() => setFormat('json')}>
              JSON 1C
            </FormatTab>
          </FormatToggleRow>

          <FileUploadRow>
            <label
              style={{
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                fontSize: 12,
                fontWeight: 600,
                padding: '6px 12px',
                borderRadius: 6,
                background: '#334155',
                color: '#ffffff',
              }}
            >
              <IconFolderOpen size={14} />
              Загрузить файл с диска (.txt / .csv / .tsv / .json)
              <input
                type="file"
                accept=".txt,.csv,.tsv,.json"
                style={{ display: 'none' }}
                onChange={(e) => void handleFileUpload(e)}
              />
            </label>
            <span style={{ fontSize: 11, opacity: 0.6 }}>или вставьте текст напрямую ниже</span>
          </FileUploadRow>

          {fileInfo && (
            <FileInfo $warn={fileInfo.count === 0} role="status">
              {fileInfo.text}
            </FileInfo>
          )}

          <TextArea
            placeholder={`Вставьте текст выгрузки 1CClientBankExchange, JSON-пакет либо выделите и скопируйте диапазон ячеек прямо из Excel (Ctrl+C → Ctrl+V сюда):
№ заявки	Контрагент	ИНН	Сумма	Назначение платежа	Р/с	БИК`}
            value={content}
            onChange={(e) => {
              setContent(e.target.value);
              // Текст правят руками — подпись о файле уже не про него
              setFileInfo(null);
            }}
          />

          <Hint>
            <IconLightbulb size={14} />
            <span>
              Парсер автоматически распознаёт разделители табуляции или точки с запятой, сопоставляет реквизиты контрагентов (ИНН, КПП, Р/с, БИК), пополняет справочник контрагентов и добавляет заявки в реестр с прогоном 4 гейтов финконтроля.
            </span>
          </Hint>
        </ModalBody>

        <ModalFooter>
          <Button $variant="secondary" onClick={onClose}>
            Закрыть
          </Button>
          <Button $variant="primary" onClick={handleImport} disabled={isLoading}>
            {isLoading ? 'Импорт…' : 'Импортировать в реестр'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
}
